<template>
  <div>
    <div
      v-if="isLoading"
      class="create-audience__loading"
    >
      <loading-dots></loading-dots>
    </div>
    <div v-if="!isLoading">
      <div class="create-target-form__name-section">
        <v-row justify="space-around">
          <v-col md="7">
            <target-name
              :isNewTarget="isNewTarget"
              :targetType="target.target"
              :initialValue="initialTarget.display_name"
              :initialTargetAction="initialTargetAction"
              :disabled="!!targetName"
              :validateAllNames="targetCreated"
              @updateName="val => (target.display_name = val)"
              @updateNameIsValid="isValid => (nameIsValid = isValid)"
            />
          </v-col>
        </v-row>
      </div>
      <v-row justify="space-around">
        <v-col md="7">
          <advertiser-select
            @update="val => (target.advertiser = val)"
            :initialValue="target.advertiser"
            :disabled="isByodTraitPresent"
            :showAdvertiserId="true"
          />
        </v-col>
      </v-row>
      <v-row justify="space-around">
        <v-col md="7">
          <streaming-select
            @input="val => (target.viewing_stream = val)"
            :initialValue="target.viewing_stream"
            :viewingOption="viewingOption"
          />
        </v-col>
      </v-row>
      <div
        v-if="isDuplicating"
        class="create-audience__button-area justify-space-around"
      >
        <action-button
          @click.native="onDuplicate"
          :disabled="!canBuildAudience || buttonClicked"
          label="Duplicate Audience"
          color="secondary"
          id="duplicateAudienceBtn"
        />
      </div>
      <div
        v-if="!isDuplicating"
        class="create-audience__button-area justify-space-around"
      >
        <action-button
          @click.native="onFile"
          :disabled="buttonClicked"
          label="attach a file"
          id="attachAFileBtn"
          color="secondary"
        />
        <action-button
          @click.native="onActivate"
          :disabled="!canActivate || buttonClicked"
          label="activate"
          id="activateAudienceBtn"
          color="secondary"
        />
        <action-button
          @click.native="onBuild"
          :disabled="!canBuildAudience || buttonClicked"
          label="build audience"
          id="buildAudienceBtn"
          color="secondary"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import TargetName from '@/appComponents/targets/createTarget/components/TargetName'
  import AdvertiserSelect from '@/appComponents/targets/components/AdvertiserSelect'
  import StreamingSelect from '@/appComponents/targets/components/StreamingSelect'
  import Modal from '@/components/common/modal'
  import LoadingDots from '@/components/common/loaderDots'
  import ActionButton from '@/components/common/actionButton'
  import { cloneDeep } from 'lodash'
  import {
    ACTIVATION_STATUS,
    INTENTS,
    DATASOURCES,
    TARGET_TYPE,
    TARGET_ACTIONS,
    ADDRESSABLE_DESTINATIONS,
    DESTINATIONS,
  } from '@/appLogic/targets/Constants'
  import {
    CREATE_TARGET,
    DUPLICATE_TARGET,
    SET_INITIAL_TARGET,
    SET_INITIAL_TARGET_ACTION,
  } from '@/appLogic/targets/createTarget/store/actions'
  import {
    SET_TARGET,
    SET_AVAILABLE_DESTINATIONS,
    SET_TARGET_CREATE_ACTION,
  } from '@/appLogic/workbench/common/store/actions'
  import { definitionToListOfTraits } from '@/helpers'

  export default {
    components: {
      TargetName,
      AdvertiserSelect,
      StreamingSelect,
      ActionButton,
      Modal,
      LoadingDots,
    },
    props: {
      onComplete: {
        type: Function,
        default: () => {},
      },
      onAudienceActivation: {
        type: Function,
        default: () => {},
      },
      targetName: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        errorMessage: '',
        modalTitle: 'New Audience',
        target: cloneDeep(this.initialTarget),
        nameIsValid: false,
        buttonClicked: false,
        isLoading: false,
      }
    },
    created() {
      this.target = cloneDeep(this.initialTarget)
    },
    computed: {
      initialTarget() {
        const defaultTarget = {
          destinations: [],
          display_name: this.targetName,
          target_datasource: null,
          advertiser: null,
          target: TARGET_TYPE.LAS,
          username: this.$store.state.auth.username,
          viewing_stream: null,
          target_definition: {},
          metadata: {
            intents:
              this.$store.state.createTarget && this.$store.state.createTarget.intents
                ? this.$store.state.createTarget.intents.map(i => INTENTS[i])
                : [],
          },
        }
        let target = { ...defaultTarget, ...cloneDeep(this.$store.state.createTarget.initialTarget) }
        return target
      },
      originalTarget() {
        return this.$store.state.createTarget.initialTarget
      },
      initialTargetAction() {
        return this.$store.state.createTarget.initialTargetAction
      },
      targetCreated() {
        return this.initialTarget.id !== 'undefined' && this.initialTarget.id !== null
      },
      isNewTarget() {
        return !this.initialTarget.id
      },
      viewingOption() {
        return 8
      },
      canActivate() {
        return (
          this.nameIsValid && this.target.advertiser && !this.isDuplicating && this.$store.getters.hasPublishPermissions
        )
      },
      canBuildAudience() {
        return this.nameIsValid
      },
      isDuplicating() {
        return this.initialTargetAction === TARGET_ACTIONS.DUPLICATE
      },
      isDigitalSocialDestination() {
        return (
          this.target.destinations &&
          this.target.destinations.some(destination => [DESTINATIONS.KRUX, DESTINATIONS.LIVERAMP].includes(destination))
        )
      },
      isByodTraitPresent() {
        if (this.target.target_definition && this.target.target_definition.query && this.originalTarget.advertiser) {
          const traits = definitionToListOfTraits(this.originalTarget.target_definition.query)
          const byodTraitMatchesAdvertiser = traits.some(
            trait => trait.advertiserId && trait.advertiserId === this.originalTarget.advertiser.Advertiser_id,
          )
          return byodTraitMatchesAdvertiser
        }
        return false
      },
    },
    methods: {
      onFile() {
        // Retain the target name when switching to upload target modal
        if (this.target.display_name) {
          let initialTarget = this.$store.getters.initialTarget
          this.$store.dispatch(SET_INITIAL_TARGET, { ...initialTarget, display_name: this.target.display_name })
        }
        let v = DATASOURCES.FILE // setting file datasource for file upload
        this.$emit('input', {
          completed: Boolean(v),
          selectedDataSource: v,
        })
      },
      setAvailableDestinations() {
        // Only LAS and Addressable Destinations can be activated
        this.$store.dispatch('workbench/' + SET_AVAILABLE_DESTINATIONS, [
          ...ADDRESSABLE_DESTINATIONS,
          DESTINATIONS.SYMPHONY,
        ])
      },
      onActivate() {
        this.buttonClicked = true
        this.target = { ...this.target, target: TARGET_TYPE.ADSTRA, xandr_data: { frequency: null } }
        this.onAudienceActivation()
        this.setAvailableDestinations()
        this.$store.dispatch(SET_INITIAL_TARGET, { ...this.target })
        this.$store.dispatch(SET_INITIAL_TARGET_ACTION, TARGET_ACTIONS.ACTIVATE)
      },
      onBuild() {
        this.buttonClicked = true
        this.target.activation_status =
          this.target.activation_status && !this.isDuplicating ? this.target.activation_status : ACTIVATION_STATUS.DRAFT
        this.target.username = this.$store.state.auth.username
        this.target.advertiser = this.target.advertiser ? this.target.advertiser : { Name: null }
        this.isLoading = true
        this.$store
          .dispatch(CREATE_TARGET, this.target)
          .then(resp => {
            this.moveToNewWorkbench(resp)
          })
          .catch(err => {
            console.error(err)
            this.isLoading = false
            this.onComplete()
          })
      },
      resetAmData() {
        this.target.am_data.status && (this.target.am_data.status = null)
        this.target.am_data.AEFlightEndDate && (this.target.am_data.AEFlightEndDate = null)
        this.target.am_data.AEFlightStartDate && (this.target.am_data.AEFlightStartDate = null)
        this.target.am_data.effectiveUEDate && (this.target.am_data.effectiveUEDate = null)
        this.target.am_data.submitted_timestamp && (this.target.am_data.submitted_timestamp = null)
        this.target.am_data.flightStartDate && (this.target.am_data.flightStartDate = null)
        this.target.am_data.flightEndDate && (this.target.am_data.flightEndDate = null)
        this.target.am_data.xdFlightOverlap && (this.target.am_data.xdFlightOverlap = 0)
        this.target.am_data.order && delete this.target.am_data.order
      },
      resetXandrData() {
        delete this.target.xandr_data.campaign
        delete this.target.xandr_data.segment
        this.target.xandr_data.hasOwnProperty('exportDayOfWeek') && delete this.target.xandr_data.exportDayOfWeek
        // No longer used
        // this.target.xandr_data.xandrDataCampaign && delete this.target.xandr_data.xandrDataCampaign
        // this.target.xandr_data.xandrDataTarget && delete this.target.xandr_data.xandrDataTarget
        if (this.target.xandr_data.external) {
          if (this.target.xandr_data.external[0]) {
            this.target.xandr_data.external[0].externalID = ''
          }
          let newXandrExternal = [...this.target.xandr_data.external]
          newXandrExternal.forEach(endpoint => {
            if (endpoint.universeEstimates) {
              endpoint.universeEstimates = null
            }
          })
          this.target.xandr_data.external = [...newXandrExternal]
        }
      },
      onDuplicate() {
        this.buttonClicked = true
        if (this.isDigitalSocialDestination) {
          this.target.viewing_stream = undefined
        }
        this.target.id = undefined
        this.target.cae_id = undefined
        this.target.activated = false
        this.target.activation_status = ACTIVATION_STATUS.DRAFT
        this.target.audience_size = 0
        this.target.destinations = []
        if (this.target.xandr_data) {
          this.resetXandrData()
        }
        if (this.target.am_data) {
          this.resetAmData()
        }
        this.target.username = this.$store.state.auth.username
        this.$store
          .dispatch(DUPLICATE_TARGET, { target: this.target, duplicate_id: this.originalTarget.id })
          .then(resp => {
            this.$store.dispatch('workbench/' + SET_TARGET_CREATE_ACTION, TARGET_ACTIONS.DUPLICATE)
            this.moveToNewWorkbench(resp)
          })
          .catch(err => {
            console.error(err)
            this.onComplete()
          })
      },
      moveToNewWorkbench(resp) {
        this.$store.dispatch('workbench/' + SET_TARGET, resp)
        this.isLoading = false
        this.onComplete()
        this.$router.push({ name: 'NewWorkbench', params: { id: resp.id } })
      },
    },
    watch: {
      initialTarget() {
        this.target = cloneDeep(this.initialTarget)
      },
    },
  }
</script>

<style lang="scss">
  .create-audience {
    .v-select {
      width: 100% !important;
    }

    &__select-label {
      text-align: center;
    }

    &__target-name {
      margin: 0px 0px 40px 0px;
      text-align: center;

      .create-target-form__name-input {
        width: 450px !important;
      }
    }

    &__options-area {
      display: flex;
      width: 50%;
      margin-left: 25%;

      &.pad {
        margin-bottom: 15px;
        margin-top: 15px;
      }
    }

    &__select-area {
      flex: 1;

      & + div {
        margin-left: 15px;
      }
    }

    &__button-area {
      margin: 30px 0 0;
      width: 35em;
      display: flex;
      flex-direction: row;
    }

    &__loading {
      display: flex;
      margin-left: 40%;
    }
  }
</style>



// WEBPACK FOOTER //
// CreateAudience.vue?867aa708